import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from 'utils/services';

import { GetHomeProductsResponse, ProductGroup } from './homeProducts.types';
import { normalizeGroups } from './homeProducts.mappers';

/**
 * RTK Query API slice for the server-driven Home product cards (`GET /pages/home/products`).
 *
 * The endpoint may `404` until the backend deploys; the base query surfaces that as an `error`, so the
 * consuming section simply renders nothing (see `HomeProducts`). The response is unwrapped and normalized
 * into ordered {@link ProductGroup}s, dropping any malformed group/card.
 */
export const homeProductsApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    getHomeProducts: build.query<ProductGroup[], void>({
      // Server-composed card states change outside this client (order ships, renewal falls due), and no
      // mutation here can invalidate the tag, so never serve a cached feed on mount — refetch instead.
      // Matches the convention in `myAccount.ts` / `calls.ts` / `settings.ts`.
      keepUnusedDataFor: 0,
      providesTags: ['HomeProducts'],
      query: () => ({ url: '/pages/home/products' }),
      transformResponse: (response: GetHomeProductsResponse) =>
        normalizeGroups(response?.data?.groups)
    })
  }),
  reducerPath: 'homeProductsApi',
  tagTypes: ['HomeProducts']
});

export const { useGetHomeProductsQuery, useLazyGetHomeProductsQuery } = homeProductsApi;
